'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Does Orbit Gum Have Microplastics? The Synthetic-Base Verdict (2026)"
      author="MicroPlastics Research Desk"
      date="July 15, 2026"
      readTime="9 min read"
      heroImage="/blog/heroes/food.webp"
      heroImageAlt="Does Orbit gum have microplastics — the Mars Wrigley synthetic gum base audited against the 2025 UCLA study"
    >
      <BlogLastUpdated iso="2026-09-21" formatted="September 21, 2026" />

      <BlogTLDR>
        <strong>Yes. Orbit is built on a synthetic gum base, and chewing it releases
        microplastics into your saliva.</strong>{' '}
        Orbit (Mars Wrigley) uses the same category of <strong>synthetic, petroleum-derived
        gum base</strong> as other mainstream brands — polymers like polyvinyl acetate
        and polyethylene, disclosed only as the words &ldquo;gum base.&rdquo; A 2025
        UCLA pilot study measured roughly <strong>100&ndash;600 microplastic particles
        per gram</strong> of chewed gum, most released in the first eight minutes. Orbit&apos;s
        &ldquo;sugar-free&rdquo; positioning is about sweeteners (sorbitol, xylitol,
        aspartame), <strong>not</strong> about the plastic base, which is unchanged. As
        with every brand, the honest note is that the same study found natural chicle
        gums shed similar amounts — so the reliable fix is chewing less, not just
        switching labels.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Orbit uses a <strong>synthetic gum base</strong> — the same petroleum-polymer category (polyvinyl acetate, polyethylene) behind most mainstream gum.</>,
          <>&ldquo;Sugar-free&rdquo; refers to the <strong>sweeteners, not the base</strong>. Orbit&apos;s plastic gum base is identical in principle to sugared gum.</>,
          <>The 2025 UCLA study found gum sheds <strong>~100&ndash;600 microplastic particles per gram</strong>; a piece is 2&ndash;6 grams, and 94% release in the first 8 minutes.</>,
          <><strong>Natural gums shed similar amounts</strong> in that study, so a chicle swap is a cleaner ingredient list, not a proven zero.</>,
          <>&ldquo;Gum base&rdquo; legally hides up to <strong>46 undisclosed ingredients</strong> — you can&apos;t read the exact polymers off the pack.</>,
          <>See the full <Link href="/blog/chewing-gum-brands-ranked-microplastics" className="text-forest underline">gum brands ranked</Link>, or the <Link href="/blog/does-trident-have-microplastics" className="text-forest underline">Trident</Link> and <Link href="/blog/does-simply-gum-have-microplastics" className="text-forest underline">Simply Gum</Link> verdicts.</>,
        ]}
      />

      <RiskQuizInlineNudge source="blog_inline_orbit" />

      <KeyStats
        heading="Orbit & microplastics — the facts"
        stats={[
          { value: 'Synthetic', label: 'Orbit gum base', context: 'petroleum-derived polymers under the "gum base" umbrella', source: 'Chewing gum ingredient labeling', sourceUrl: 'https://www.acs.org/pressroom/presspacs/2025/march/chewing-gum-can-shed-microplastics-into-saliva-pilot-study-finds.html' },
          { value: '100–600/g', label: 'microplastics per gram of gum', context: '2025 UCLA pilot study across 10 brands', source: 'Mohanty / UCLA, ACS Spring 2025', sourceUrl: 'https://newsroom.ucla.edu/releases/bursting-your-bubble-chewing-gum-releases-microplastics-into-your-saliva-ucla-research-shows' },
          { value: 'Sugar-free ≠', label: 'plastic-free', context: 'sugar-free is about sweeteners; the plastic base is unchanged', source: 'Product labeling', sourceUrl: 'https://www.fda.gov/food' },
          { value: '94%', label: 'shed in first 8 minutes', context: 'most particles release early in the chew', source: 'UCLA 2025 pilot study', sourceUrl: 'https://www.acs.org/pressroom/presspacs/2025/march/chewing-gum-can-shed-microplastics-into-saliva-pilot-study-finds.html' },
        ]}
      />

      <h2>Does Orbit gum contain microplastics?</h2>
      <p>
        Yes. Orbit&apos;s chew comes from a <strong>synthetic gum base</strong> — the
        same family of food-grade plastics used across mainstream gum, including
        polyvinyl acetate and polyethylene. US labeling rules let all of it appear as
        the single term <strong>&ldquo;gum base,&rdquo;</strong> an umbrella that can
        cover up to 46 permitted ingredients without naming any individually. So Orbit
        is, quite literally, partly plastic — and the 2025 UCLA pilot study led by
        Sanjay Mohanty showed that chewing gum transfers those particles into saliva:
        roughly <strong>100&ndash;600 microplastic particles per gram</strong>, most in
        the first eight minutes.
      </p>

      <h2>&ldquo;Sugar-free&rdquo; doesn&apos;t mean plastic-free</h2>
      <p>
        Orbit is marketed heavily on being sugar-free, and that is true — it uses
        sugar alcohols and high-intensity sweeteners (sorbitol, xylitol, mannitol,
        aspartame) instead of sugar. But <strong>&ldquo;sugar-free&rdquo; describes the
        sweetener, not the base.</strong> The plastic that makes Orbit chewable is
        unchanged whether the gum is sugared or sugar-free. Reading &ldquo;sugar-free&rdquo;
        as &ldquo;clean&rdquo; is exactly the kind of label shortcut that hides the
        actual microplastic source.
      </p>

      <h2 id="xylitol">Does Orbit gum have xylitol?</h2>
      <p>
        <strong>Yes, all of it.</strong> On the ingredient lists Orbit publishes on
        its own US site (checked 21 September 2026), Spearmint, Peppermint and Sweet
        Mint all list xylitol among the ingredients present at under 2%, after
        sorbitol, gum base, glycerol and mannitol. Orbit White lists xylitol{' '}
        <strong>first</strong>, so by weight it is that gum&apos;s main ingredient.
      </p>
      <p>
        That matters most if you have a dog. Xylitol is a permitted sweetener for
        people but dangerous to dogs: the FDA says it can trigger a rapid, profound
        drop in blood sugar within 10 to 60 minutes, and that some serious effects
        may not appear for 12 to 24 hours. If your dog has eaten any Orbit gum, call
        your vet, an emergency animal hospital, or the ASPCA Animal Poison Control
        Center on <strong>(888) 426-4435</strong> straight away rather than waiting
        for symptoms.
      </p>

      <h2>The honest note on switching</h2>
      <p>
        If you want to drop the petroleum-plastic ingredient, a{' '}
        <Link href="/blog/does-simply-gum-have-microplastics" className="text-forest underline">chicle-based gum</Link>{' '}
        (tree-sap base) gives you a genuinely cleaner label. Just don&apos;t expect it to
        end microplastic shedding: the UCLA study found natural and synthetic gums
        released <strong>similar</strong> particle counts. The only change proven to cut
        your gum-derived microplastics is chewing fewer pieces, for less time.
      </p>

      <BlogComparisonTable
        caption="Orbit vs lower-exposure options"
        columns={[
          { key: 'option', label: 'Option' },
          { key: 'base', label: 'Gum base' },
          { key: 'note', label: 'Note' },
        ]}
        rows={[
          { option: 'Orbit / Trident / Extra / 5', base: 'Synthetic (petroleum polymers)', note: 'Plastic base; sheds microplastics' },
          { option: 'Chicle gum (Simply Gum, Glee, Chicza)', base: 'Chicle (tree sap)', note: 'Cleaner ingredients; still sheds particles' },
          { option: 'Chew less / shorter', base: 'n/a', note: 'The only proven reduction' },
        ]}
      />
      <p>
        Full context, with every major brand&apos;s gum base, is in the{' '}
        <Link href="/blog/chewing-gum-brands-ranked-microplastics" className="text-forest underline">gum brands ranked</Link>{' '}
        guide.
      </p>

      <BlogAppCTA
        headline="Gum is a hidden source — see the obvious ones"
        sub="The app can't scan gum, but it can scan the packaged foods and drinks around it. Find where plastic actually enters your day with the MicroPlastics app."
      />

      <BlogFAQ
        items={[
          {
            question: 'Is Orbit gum made of plastic?',
            answer: 'Partly. Orbit\'s "gum base" is a synthetic, petroleum-derived polymer blend (such as polyvinyl acetate and polyethylene) that gives the gum its chew. It is food-grade, but it is plastic.',
          },
          {
            question: 'Does sugar-free Orbit still have microplastics?',
            answer: 'Yes. Sugar-free refers only to the sweeteners used in place of sugar. The synthetic gum base — the source of the microplastics — is unchanged in sugar-free gum.',
          },
          {
            question: 'How many microplastics does Orbit release?',
            answer: 'The 2025 UCLA pilot study found chewing gum releases about 100 to 600 microplastic particles per gram of gum into saliva, with 94% released in the first eight minutes. A piece typically weighs 2 to 6 grams.',
          },
          {
            question: 'Does Orbit gum have xylitol?',
            answer: 'Yes. Orbit\'s own US ingredient lists show xylitol in Spearmint, Peppermint and Sweet Mint (under 2%) and as the first ingredient in Orbit White. Xylitol is dangerous to dogs: if a dog eats Orbit, call a vet, an emergency animal hospital or the ASPCA Animal Poison Control Center on (888) 426-4435 right away.',
          },
          {
            question: 'Is chicle gum better than Orbit?',
            answer: 'Chicle gum has a cleaner, plant-based ingredient list without petroleum polymers. But the UCLA study found natural and synthetic gums shed similar microplastic amounts, so it improves the ingredients rather than guaranteeing zero shedding. Chewing less is the reliable reduction.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'Mohanty S, et al. (UCLA)', year: 2025, title: 'Chewing Gums: Unintended sources of ingested microplastics in humans (ACS Spring 2025)', publisher: 'American Chemical Society', url: 'https://www.acs.org/pressroom/presspacs/2025/march/chewing-gum-can-shed-microplastics-into-saliva-pilot-study-finds.html' },
          { authors: 'UCLA Newsroom', year: 2025, title: 'Chewing gum releases microplastics into your saliva, UCLA research shows', publisher: 'UCLA', url: 'https://newsroom.ucla.edu/releases/bursting-your-bubble-chewing-gum-releases-microplastics-into-your-saliva-ucla-research-shows' },
          { authors: 'Orbit (Mars Wrigley)', year: 2026, title: 'Orbit product ingredient lists (Spearmint, Peppermint, Sweet Mint, Orbit White)', publisher: 'orbitgum.com', url: 'https://www.orbitgum.com/our-products' },
          { authors: 'US Food and Drug Administration', year: 2025, title: 'Paws Off Xylitol; It\'s Dangerous for Dogs', publisher: 'FDA Consumer Updates', url: 'https://www.fda.gov/consumers/consumer-updates/paws-xylitol-its-dangerous-dogs' },
          { authors: 'World Health Organization', year: 2022, title: 'Dietary and inhalation exposure to nano- and microplastic particles', publisher: 'WHO', url: 'https://www.who.int/publications/i/item/9789240054608' },
        ]}
      />
    </BlogPostLayout>
  )
}
